import React, { useState } from 'react';
import { Box, Button, ContentTemplate, Divider, Progress } from 'web-library';
import { useHistory, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';


import { makeStyles } from 'tss-react/mui';
import { StatusCodes } from 'http-status-codes';
import { candidateService } from '../../../api';
import RouteKey from '../../../routes/key';
import ApplicationHeaderDetails from '../../examRegistration/components/summary/ApplicationHeaderDetails';
import PersonalDetails from '../../examRegistration/components/particulars/PersonalDetails';
import ContactDetails from '../../examRegistration/components/particulars/ContactDetails';
import NextOfKinContactDetails from '../../examRegistration/components/particulars/NextOfKinContactDetails';
import ExamSelectedSubjects from '../../examRegistration/components/ExamReviewSubjects';
import ExamFeeDetails from '../../../components/ExamFeeDetails';
import ExamTransactions from '../../examRegistration/components/summary/ExamTransactions';
import { FileUploadState } from '../../../models/File';
import DownloadProgressModal from '../../../components/modals/DownloadProgressModal';

interface ExamSummaryPathParams {
  uuid: string;
}

const useStyles = makeStyles()((theme) =>
({
  container: {
    alignItems: 'center',
  },
  errorText: {
    color: theme.color.state.stateError,
  },
  dialogBold: {
    fontWeight: 'bold',
  },
  paymentButton: {
    padding: theme.spacing(2),
    marginRight: theme.spacing(2),
  },
  tableCaption: {
    paddingTop: theme.spacing(2),
    paddingBottom: theme.spacing(24),
  },
  examFeeDetails: {
    paddingBottom: theme.spacing(6),
  },
  selectedSubjects: {
    paddingTop: theme.spacing(6),
  },
  button: {
    marginBottom: theme.spacing(7),
    height: 'fit-content',
  },
}));

const { exam, error } = RouteKey.child;
const { generic } = RouteKey.error.child;
const { examRegistrations } = RouteKey.amendment.child;
const { FORBIDDEN } = StatusCodes;

const TransactionHistoryExamRegistration = () => {
  const { uuid } = useParams<ExamSummaryPathParams>();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const history = useHistory();
  const [fileStatus, setFileStatus] = useState(FileUploadState.IDLE);

  const {
    refetch: downloadCandidateSummary,
    isFetching,
  } = candidateService.exam.candidate.useGetCandidateDownloadSummary(
    (download) => {
      const newWindow = window.open(download?.url, '_blank', 'noopener,noreferrer');
      if (newWindow) {
        newWindow.opener = null;
      }
    },
    (_) => {
      setFileStatus(FileUploadState.FAILED);
    },
    uuid
  );

  const {
    data: examSummary,
    isLoading: isLoadingExamSummary,
  } = candidateService.exam.candidate.useGetExamRegistrationSummary(uuid, (errors) => {
    if (errors.response?.status === FORBIDDEN) {
      history.push(`/${error}/${generic}`);
    }
  });

  const handleCloseDownloadErrorPrompt = () => {
    setFileStatus(FileUploadState.IDLE);
  };

  if (isLoadingExamSummary) {
    return (
      <ContentTemplate>
        <Progress color="secondary" />
      </ContentTemplate>
    );
  }

  return (
    <>
      {examSummary && (
        <>
          <ContentTemplate isContinuable type="full">
            <ApplicationHeaderDetails
              onDownloadAction={downloadCandidateSummary}
              isDownloading={isFetching}
              applicationDetails={examSummary.applicationStatus}
              displayRegistrationType
              isDisplayEntryProof={false}
            />

            <PersonalDetails examCandidateDetail={examSummary.candidateDetails} />
            <ContactDetails contactDetails={examSummary.candidateDetails} />
            <NextOfKinContactDetails {...examSummary.candidateDetails} />
            <ExamSelectedSubjects className={classes.selectedSubjects} {...examSummary} />
            <ExamFeeDetails className={classes.examFeeDetails} {...examSummary} />
            <Divider />
            <ExamTransactions
              {...examSummary}
              candidateName={examSummary.candidateDetails.name || ''}
            />
          </ContentTemplate>
          <ContentTemplate continuous>
            <Box>
              <Button
                id="button-selectSubject-back"
                variant="outlined"
                className={classes.button}
                onClick={() =>
                  history.push(`/${exam}/${examRegistrations.route}`, {
                    from: 'TransactionHistoryPages',
                  })
                }
              >
                {t('exam->>RegistrationSummary.BackToTransactionHistory')}
              </Button>
            </Box>
          </ContentTemplate>
          {fileStatus === FileUploadState.FAILED && (
            <DownloadProgressModal
              fileDownloadStatus={fileStatus}
              modalState
              onDialogClose={handleCloseDownloadErrorPrompt}
            />
          )}
        </>
      )}
    </>
  );
};

export default TransactionHistoryExamRegistration;
